import React from 'react'
import ReactMarkdown from 'utils/markdown'
import { UserContext } from 'utils/context/UserContext'
import { useSelector } from 'react-redux'
import { GlobalState } from 'types'
import { getLoggedInJwt } from 'packages/authentication'
import { getCharacters } from 'services/user/characters'
import { Character } from 'types/account'
import { ClipLoader } from 'react-spinners'
import { getPlainTextFromRichText } from '../../utils'
import { DonationCampaign, DonationFormProps } from './types'
import ThankYouMessage from '../thank-you-message'
import style from './DonationForm.module.scss'
// Import directly from the module file to avoid circular references
import { useDebugFeatures } from '../debug-controls/useDebugFeatures'
import DebugPanel from '../debug-controls/DebugPanel'
import {
    processDonation,
    validateDonation,
    ValidationErrors,
    ERROR_CODE_TO_TRANSLATION_KEY,
} from './donation-service'
import { DonationFormContent } from './components'

/**
 * DonationForm component handles the entire donation flow
 * including validation, submission, and thank you message
 */
const DonationForm = ({
    campaigns,
    translations,
}: DonationFormProps): JSX.Element => {
    const { plexBalance, updatePlex } = React.useContext(UserContext)
    const [
        selectedCampaign,
        setSelectedCampaign,
    ] = React.useState<DonationCampaign>(campaigns[0])
    const [
        selectedCharacterId,
        setSelectedCharacterId,
    ] = React.useState<number>()
    const [donationAmount, setDonationAmount] = React.useState<number>(0)
    const userToken = useSelector((state: GlobalState) => getLoggedInJwt(state))
    const [characters, setCharacters] = React.useState<Character[]>([])
    const [loading, setLoading] = React.useState(false)
    const [fetchingCharacters, setFetchingCharacters] = React.useState(false)
    const [donationStatus, setDonationStatus] = React.useState<
        'idle' | 'success' | 'error'
    >('idle')
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
    const [errorCode, setErrorCode] = React.useState<string | null>(null)
    const [fieldErrors, setFieldErrors] = React.useState<ValidationErrors>({})

    // Use debug features hook
    const debugFeatures = useDebugFeatures({
        userToken,
        setDonationStatus,
        setErrorMessage,
        setErrorCode,
        setFieldErrors,
        setLoading,
        updatePlex,
        setCharacters,
        setSelectedCharacterId,
        setDonationAmount,
        translations,
        // Current values
        selectedCampaign,
        donationAmount,
        plexBalance,
        characters,
        selectedCharacterId,
        errorCode,
    })

    // Use isUsingRealCharacters from debug features if available
    const isUsingRealCharacters = debugFeatures?.isUsingRealCharacters ?? true

    // Load characters when component mounts or token changes
    React.useEffect(() => {
        if (userToken && isUsingRealCharacters) {
            console.log('Fetching characters with token...')
            setFetchingCharacters(true)
            getCharacters(userToken)
                .then((response) => {
                    console.log('Characters API response:', response)
                    if (response.success === true) {
                        setCharacters(response.data.characters)
                    } else {
                        if (response.errorCode === 'CharacterRetrievalError') {
                            setErrorCode((prev) => {
                                return prev || 'CharacterRetrievalError'
                            })
                            setErrorMessage(
                                (prev) =>
                                    prev ||
                                    'The server is experiencing issues. Please try again later.'
                            )
                        }
                        setCharacters([])
                    }
                })
                .finally(() => {
                    setFetchingCharacters(false)
                })
        }
    }, [userToken, isUsingRealCharacters])

    React.useEffect(() => {
        if (plexBalance === 'N/A') {
            setErrorCode('FetchPlexBalanceError')
            setErrorMessage(
                'Failed to fetch PLEX balance, please try again later'
            )
        }
    }, [plexBalance])

    // Reset error states when donation amount or character changes
    React.useEffect(() => {
        if (donationStatus !== 'idle') {
            setDonationStatus('idle')
            setErrorMessage(null)
            setErrorCode(null)
            setFieldErrors({})
        }
    }, [donationAmount, selectedCharacterId])

    // Helper function to get translated error message from error code
    const getTranslatedErrorMessage = (code: string | null): string | null => {
        if (!code || !translations) return null

        const translationKey =
            ERROR_CODE_TO_TRANSLATION_KEY[code] ||
            ERROR_CODE_TO_TRANSLATION_KEY._default
        if (translationKey && translations[translationKey]) {
            return getPlainTextFromRichText(translations[translationKey])
        }
        return null
    }

    const handleDonate = (): void => {
        // Reset error states
        setDonationStatus('idle')
        setErrorMessage(null)
        setErrorCode(null)
        setFieldErrors({})

        console.log(
            'handleDonate: ',
            donationAmount,
            selectedCampaign,
            selectedCharacterId,
            debugFeatures?.shouldUseSimulation
                ? `Simulation mode: ${debugFeatures.simulatedResponse}`
                : 'Using real API'
        )

        // If simulation mode is active, use that instead of a real API call
        if (debugFeatures?.shouldUseSimulation) {
            console.log(
                'Simulating donation with type:',
                debugFeatures.simulatedResponse
            )
            debugFeatures.handleSimulateDonation(
                debugFeatures.simulatedResponse
            )
            return
        }

        // Validate before submission
        const validationErrors = validateDonation(
            donationAmount,
            plexBalance,
            selectedCampaign,
            selectedCharacterId,
            characters.length,
            translations
        )

        if (Object.keys(validationErrors).length > 0) {
            setFieldErrors(validationErrors)
            setDonationStatus('error')
            return
        }

        if (
            !loading &&
            donationAmount &&
            selectedCampaign &&
            selectedCharacterId
        ) {
            console.log('Making real API donation request')
            processDonation(
                userToken,
                {
                    plexAmount: donationAmount,
                    campaignId: selectedCampaign.id,
                    characterId: selectedCharacterId,
                },
                // onStart callback
                () => {
                    setLoading(true)
                },
                // onComplete callback
                (success, errors = {}, message, errCode) => {
                    setDonationStatus(success ? 'success' : 'error')
                    if (success) {
                        updatePlex(userToken)
                    } else {
                        if (message) {
                            setErrorMessage(message)
                        }
                        if (errCode) {
                            setErrorCode(errCode)
                            console.log('Error code detected:', errCode)
                        }
                        setFieldErrors(errors)
                    }
                    setLoading(false)
                },
                translations
            )
        }
    }

    return (
        <div className={style.container} data-testid="donation-form">
            <div className={style.imageContainer}>
                <ReactMarkdown
                    className={style.description}
                    source={selectedCampaign.description}
                />
                <img
                    alt={selectedCampaign?.imageFile?.description}
                    src={selectedCampaign?.imageFile?.url}
                />
            </div>
            <div className={style.formContainer}>
                {donationStatus === 'success' ? (
                    <ThankYouMessage translations={translations} />
                ) : (
                    <DonationFormContent
                        translations={translations}
                        selectedCampaign={selectedCampaign}
                        setSelectedCampaign={setSelectedCampaign}
                        campaigns={campaigns}
                        donationAmount={donationAmount}
                        setDonationAmount={setDonationAmount}
                        plexBalance={plexBalance}
                        selectedCharacterId={selectedCharacterId}
                        setSelectedCharacterId={setSelectedCharacterId}
                        characters={characters}
                        loading={loading}
                        fetchingCharacters={fetchingCharacters}
                        handleDonate={handleDonate}
                        errorMessage={errorMessage}
                        errorCode={errorCode}
                        fieldErrors={fieldErrors}
                        getTranslatedErrorMessage={getTranslatedErrorMessage}
                    />
                )}

                {loading && (
                    <div className={style.loadingContainer}>
                        <ClipLoader size={80} color="#F67C0F" loading />
                    </div>
                )}
            </div>

            {/* Render debug panel if debug features are available */}
            {debugFeatures && <DebugPanel debugFeatures={debugFeatures} />}
        </div>
    )
}

export default DonationForm
